import { Button, Heading, Link, Section } from 'components'
import React from 'react'
import { RiArrowRightSLine } from 'react-icons/ri'
import Clients from './Clients'

const Faq = ({ title, question }) => (
  <div className="bg-white p-8 border border-gray-200 rounded">
    <Heading level="4" className="mb-2">
      {title}
    </Heading>
    <div>{question}</div>
  </div>
)

const PricingTable = ({
  title,
  price,
  description,
  list = [],
  button,
  primary,
}) => (
  <div className="border rounded overflow-hidden border-gray-300 text-center">
    <div
      className={`${
        primary ? 'bg-primary-500' : 'bg-gray-700'
      } p-3 m-0 text-white uppercase tracking-widest`}
    >
      {title}
    </div>

    <div className="p-8">
      <div className="text-7xl">
        <span className="text-2xl">€</span>
        <span className="font-bold">{price}</span>
        <span className="text-2xl text-gray-400">/mo</span>
      </div>
      <div className="text-gray-400 my-2">billed anually</div>
      <p className="my-8">{description}</p>
      <hr className="separator" />
      <div>
        <ul className="text-left list-inside list-disc space-y-2">
          {list.map((item) => (
            <>
              {typeof item === 'string' ? (
                <li>{item}</li>
              ) : (
                <li className="list-none ">
                  <ul className="list-disc space-y-2 list-inside ml-8">
                    {item.map((innerList) => (
                      <li>{innerList}</li>
                    ))}
                  </ul>
                </li>
              )}
            </>
          ))}
        </ul>
      </div>
      <div className="mt-12 mb-8">{button}</div>
    </div>
  </div>
)

const TempPricing = () => {
  return (
    <>
      <Section>
        <div className="grid grid-cols-12 items-center gap-24">
          <div className="col-span-5 space-y-6">
            <Heading type="kicker">Select your plan</Heading>
            <Heading>More Data. More Insights. More Revenue.</Heading>
            <p className="text-xl">LeadBoxer comer with two plans:</p>
            <p>
              <strong>Basic</strong> is best suited for smaller organizations
              that want to identify leads and customers, by tracking web and
              email behaviour.
            </p>
            <p>
              <strong>Premium</strong> is geared towards larger organizations,
              and is specifically suited if you are looking to solve your lead &
              customer data challenge.
            </p>
          </div>

          <div className="col-span-7 items-center">
            <div className="items-center grid grid-cols-2">
              <PricingTable
                title="Basic"
                price="195"
                description="Basic includes everything you need to start tracking and identifying
        your Leads and Customers."
                list={[
                  'Unlimited leads / contacts',
                  'Segments, Leadscore, notifications',
                  'Track web & email behaviour',
                  '5 users / email clients',
                  '50.000 events included',
                  'Standard integrations',
                  'Technical support',
                ]}
                button={
                  <Button type="outline" to="/start">
                    Start trial
                  </Button>
                }
              />
              <PricingTable
                title="Premium"
                price="495"
                primary
                description="Premium includes advanced or custom integrations/ bespoke reporting + all basic features."
                list={[
                  'All features from Basic plan',
                  'Premium & custom integrations',
                  'Premium firmographic data:',
                  [
                    'Email addresses',
                    'Website technologies',
                    'Revenue estimates',
                  ],
                  'Prioritized technical support',
                  'Custom dashboarding',
                  'Cross-domain tracking',
                  '250.000 events included',
                  '20 users / email clients',
                ]}
                button={
                  <Button type="primary" to="/demo">
                    Talk to sales
                  </Button>
                }
              />
            </div>
            <div className="separator max-w-4xl mx-auto mb-16 mt-8">
              <p>
                High Volume or custom price?{' '}
                <Link
                  className="text-gray-700 hover:text-primary-500"
                  to="/case-studies"
                >
                  Contact us for special deals.
                  <RiArrowRightSLine className="inline-flex" />
                </Link>
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-16 border-t border-gray-200 py-16 mt-16 ">
          <div className="space-y-2">
            <Heading level="5" className="mb-2">
              What is an event?
            </Heading>
            <p>
              Any behavioural signal we receive. For example a pageview, button
              click, email open, email click, form submit, download, application
              login, newsletter signup etc.
            </p>
          </div>

          <div className="space-y-2">
            <Heading level="5" className="mb-2">
              What is a lead or account?
            </Heading>
            <p>
              A lead is an individual /person, this can be a consumer or someone
              from a business. An Account is an organisation that consists of a
              group of individuals (leads)
            </p>
          </div>

          <div className="space-y-2">
            <Heading level="5" className="mb-2">
              What is a custom integration?
            </Heading>
            <p>
              We support many kinds of integrations, connections, etc. If yours
              is not listed or you use a custom solution, we will build and
              maintain a connector for you.
            </p>
          </div>

          <div className="space-y-2">
            <Heading level="5" className="mb-2">
              No Lead Limits
            </Heading>
            <p>
              The more you use LeadBoxer, the more value you will get. For that
              reason we do not set limits on the number of leads, contacts (or
              identified companies), notifications, triggers, segments, etc.
            </p>
          </div>

          <div className="space-y-2">
            <Heading level="5" className="mb-2">
              Scalable solution
            </Heading>
            <p>
              Our platform was built to scale, perform and deliver. Your
              specific company requirements determine the value based pricing.
            </p>
          </div>
        </div>
      </Section>

      <Section background="light">
        <div className="max-w-2xl mx-auto text-center space-y-8">
          <Heading type="kicker">Frequently Asked Questions</Heading>
          <Heading>
            Here you will find answers to the questions we hear most.
          </Heading>
        </div>

        <div className="grid grid-cols-3 gap-8 my-16">
          <Faq
            title="How does the free trial work?"
            question="It’s free. Talk to us and you will receive instructions on how to get started. You can get immediate results. Measurement is real-time. At the end of the trial period, you will be given the option to activate with a subscription."
          />

          <Faq
            title="How do I get started?"
            question="Schedule a call and receive instructions to start tracking your site and email – start generating leads immediately. After that  we will advise you further, for example about how to collect additional information, or what to do with your data."
          />

          <Faq
            title="Is this tool best for sales or marketing?"
            question="The lines between Sales and Marketing are increasingly blurred, so we would say both. We help you get qualified leads through marketing activities to pass these on to sales. Does that answer your question?"
          />

          <Faq
            title="Will I actually see the individuals who are ready to buy?"
            question="Yes. By connecting your existing web, email and other tools we can actually tell you which individual from your mail list, Newsletter or other source is Ready-to-Buy."
          />

          <Faq
            title="How many contacts can I add/ track?"
            question="We do not limit-based on number of contacts, but do have a fair-use policy for all the traffic or activity we collect. Lets say you don’t have to worry about it until you have a very large contact database (>10.000 contacts)"
          />

          <Faq
            title="How many sites and users can I add?"
            question="This depends on your plan. During your trial you can add all your colleagues and company websites to LeadBoxer, as long as you are not reselling there is no hard limit."
          />
        </div>
      </Section>

      <Section>
        <div className="text-center space-y-4 py-16">
          <Heading type="kicker">Clients</Heading>
          <Heading>Our Happy Customers</Heading>
          <Clients dividerText={false} />
        </div>

        <div className="text-center mx-auto mb-16 mt-3">
          <p>
            Looking to resell LeadBoxer? We have plans made specifically for
            Resellers, small agencies and consultants.{' '}
            <Link
              className="text-gray-700 hover:text-primary-500"
              to="/case-studies"
            >
              Schedule a call
              <RiArrowRightSLine className="inline-flex" />
            </Link>
          </p>
        </div>
      </Section>
    </>
  )
}

export default TempPricing
